import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, Card, Screen, Text } from "@andrum/ui";
import { getSupabase } from "@/lib/supabase/client";
import { listRecentCheckIns } from "@/features/check-in/api/check-in-api";
import { listOutcomes } from "@/features/experiments/api/experiments-api";
import { deleteAccountRemote } from "@/features/coach/api/coach-api";
import { useAuthStore } from "@/stores/auth-store";

export function PrivacyScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const exportData = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const supabase = getSupabase();
      const checkIns = await listRecentCheckIns(100);
      const outcomes = await listOutcomes();
      let profile = null;
      let goals: unknown[] = [];
      let assignments: unknown[] = [];
      let enrollments: unknown[] = [];
      let userMeta: { id: string; email: string | undefined } | null = null;

      if (supabase) {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          userMeta = { id: user.id, email: user.email };
          const [profileRes, goalsRes, assignRes, enrollRes] =
            await Promise.all([
              supabase.from("profiles").select("*").eq("id", user.id).maybeSingle(),
              supabase
                .from("user_goals")
                .select("goal_code, active, created_at")
                .eq("user_id", user.id),
              supabase
                .from("experiment_assignments")
                .select(
                  "id, experiment_id, status, recommendation_reason_code, created_at, completed_at, skipped_at"
                )
                .eq("user_id", user.id)
                .order("created_at", { ascending: false })
                .limit(100),
              supabase
                .from("program_enrollments")
                .select("program_id, current_day, started_at, completed_at")
                .eq("user_id", user.id),
            ]);
          profile = profileRes.data;
          goals = goalsRes.data ?? [];
          assignments = assignRes.data ?? [];
          enrollments = enrollRes.data ?? [];
        }
      }

      const blob = new Blob(
        [
          JSON.stringify(
            {
              exportedAt: new Date().toISOString(),
              app: "andrum",
              user: userMeta,
              profile,
              goals,
              checkIns,
              assignments,
              outcomes,
              programEnrollments: enrollments,
            },
            null,
            2
          ),
        ],
        { type: "application/json" }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `andrum-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setStatus(t("privacy.exportOk"));
    } catch {
      setStatus(t("privacy.exportFail"));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      setStatus(t("privacy.deleteConfirm"));
      return;
    }
    setBusy(true);
    setStatus(null);
    const result = await deleteAccountRemote();
    setBusy(false);
    if (!result.ok) {
      setStatus(result.message);
      return;
    }
    clear();
    const supabase = getSupabase();
    await supabase?.auth.signOut();
    setStatus(t("privacy.deleteOk"));
    void navigate({ to: "/welcome" });
  };

  return (
    <Screen>
      <Text variant="title" as="h1">
        {t("privacy.title")}
      </Text>
      <Text variant="muted">{t("privacy.lead")}</Text>

      <Card className="space-y-3">
        <Text variant="heading" as="h2">
          {t("privacy.exportTitle")}
        </Text>
        <Text variant="subtle">{t("privacy.exportBody")}</Text>
        <Button fullWidth loading={busy} onClick={() => void exportData()}>
          {t("privacy.exportCta")}
        </Button>
      </Card>

      <Card className="space-y-3">
        <Text variant="heading" as="h2">
          {t("privacy.deleteTitle")}
        </Text>
        <Text variant="subtle">{t("privacy.deleteBody")}</Text>
        <Button
          fullWidth
          variant="danger"
          loading={busy}
          onClick={() => void handleDelete()}
        >
          {confirmDelete ? t("privacy.deleteConfirm") : t("privacy.deleteCta")}
        </Button>
      </Card>

      {status ? (
        <Text variant="caption" className="text-center">
          {status}
        </Text>
      ) : null}
    </Screen>
  );
}
